// frontend/src/features/tbs/components/JobPanel.jsx
// Center panel for a run: progress, actions, one row per account with
// status, the tax year actually on the statement, and the real error text.
import React, { useState } from "react";
import {
  CheckCircle2, AlertCircle, Clock, Loader2, Download, FileDown, FileSpreadsheet, RotateCcw,
  Square, Trash2, ExternalLink, ShieldAlert, Info,
} from "lucide-react";

const ERROR_LABEL = {
  invalid_account: "Bad account format",
  not_found: "No statement found",
  blocked: "Blocked by Cloudflare",
  worker_error: "Scrape worker problem",
  timeout: "Timed out",
  unavailable: "Collector unavailable",
  site_error: "Site error",
  interrupted: "Interrupted",
};

function fmtDuration(ms) {
  if (ms == null) return "";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function RowStatus({ r, taxYear }) {
  if (r.status === "running") return <span className="tbs-pill info"><Loader2 size={11} className="tbs-spin" /> fetching</span>;
  if (r.status === "pending") return <span className="tbs-pill mute"><Clock size={11} /> queued</span>;
  if (r.status === "cancelled") return <span className="tbs-pill mute">cancelled</span>;
  if (r.status === "error") return <span className="tbs-pill err" title={r.error_message || ""}><AlertCircle size={11} /> {ERROR_LABEL[r.error_code] || "Failed"}</span>;
  // saved
  const wrongYear = taxYear && r.year_detected && r.year_detected !== taxYear;
  const noMatch = r.account_match === false;
  if (noMatch) return <span className="tbs-pill err" title="The account you asked for does not appear in the statement text"><ShieldAlert size={11} /> wrong account?</span>;
  if (wrongYear) return <span className="tbs-pill warn" title={`Statement is for ${r.year_detected}, you asked for ${taxYear}`}><AlertCircle size={11} /> {r.year_detected} bill</span>;
  return <span className="tbs-pill ok"><CheckCircle2 size={11} /> saved</span>;
}

function Scraped({ s }) {
  if (!s) return null;
  if (s.parse_ok === false) return <div className="tbs-detail" style={{ color: "#C75454" }}>Bill data could not be parsed: {(s.warnings || []).join("; ")}</div>;
  const money = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" }));
  return (
    <div className="tbs-detail">
      <b>{s.owner || "Owner ?"}</b> · {s.property_location || "location ?"} · tax year <b>{s.tax_year || "?"}</b><br />
      taxable <b>{s.taxable_value != null ? Number(s.taxable_value).toLocaleString() : "?"}</b>{s.taxable_value_varies ? " (varies by entity)" : ""} ·
      calc. tax <b>{money(s.total_tax_calculated)}</b> · amount due <b>{money(s.amount_due)}</b>
      {(s.entities || []).length > 0 && (<><br />{s.entities.map((e) => `${e.name} ${e.rate ?? "?"}`).join(" · ")}</>)}
      {(s.warnings || []).length > 0 && <><br /><span style={{ color: "#C48A1A" }}>{s.warnings.join("; ")}</span></>}
    </div>
  );
}

export default function JobPanel({ job, collectorsByKey, onCancel, onRetry, onDelete, onZip, onZipYear, onData, onOpenPdf, onDownloadPdf, actionBusy }) {
  const [expanded, setExpanded] = useState({});
  if (!job) return null;
  const rows = job.rows || [];
  const running = job.status === "running" || job.status === "queued";
  const total = Math.max(1, job.total || rows.length || 1);
  const ok = job.ok_count || 0, fail = job.fail_count || 0;
  const inflight = rows.filter((r) => r.status === "running").length;
  const yearWarn = rows.filter((r) => r.status === "saved" && job.tax_year && r.year_detected && r.year_detected !== job.tax_year).length;
  // Second ZIP that leaves out the statements flagged as another year. Only
  // offered when at least one row is flagged; unreadable years stay in.
  const yearUnknown = rows.filter((r) => r.status === "saved" && !r.year_detected).length;
  const yearMatch = ok - yearWarn;
  const offerYearZip = !!job.tax_year && yearWarn > 0 && yearMatch > 0;
  const acctWarn = rows.filter((r) => r.status === "saved" && r.account_match === false).length;
  const hasScraped = rows.some((r) => r.scraped);
  const canRetry = !running && rows.some((r) => r.status === "error" || r.status === "cancelled");
  const elapsed = job.started_at ? (new Date((job.finished_at || new Date().toISOString()).replace(/Z?$/, "Z")) - new Date(job.started_at.replace(/Z?$/, "Z"))) : null;

  return (
    <div className="tbs-card" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div className="tbs-card-hd">
        <div>
          <h2>{job.label || `Run #${job.id}`}</h2>
          <div className="sub">
            {fmtWhen(job.created_at)}{job.created_by ? ` · ${job.created_by}` : ""}
            {job.tax_year ? ` · checking for ${job.tax_year} bills` : " · any year"}
            {elapsed != null ? ` · ${fmtDuration(elapsed)}` : ""}
            {job.bytes_total ? ` · ${fmtBytes(job.bytes_total)} stored` : ""}
          </div>
        </div>
        <span className="spacer" />
        <div className="tbs-actions">
          {running && <button className="tbs-btn sm danger" onClick={onCancel} disabled={actionBusy || job.cancel_requested}><Square size={12} /> {job.cancel_requested ? "Stopping…" : "Stop"}</button>}
          {canRetry && <button className="tbs-btn sm" onClick={onRetry} disabled={actionBusy}><RotateCcw size={12} /> Retry failed</button>}
          {hasScraped && <button className="tbs-btn sm" onClick={onData} disabled={actionBusy}><FileSpreadsheet size={12} /> Bill data .xlsx</button>}
          {offerYearZip && <button className="tbs-btn sm" onClick={onZipYear} disabled={actionBusy} title={`Leaves out the ${yearWarn} statement${yearWarn === 1 ? "" : "s"} flagged as another year (listed in manifest.json).${yearUnknown ? ` ${yearUnknown} with no readable year ${yearUnknown === 1 ? "is" : "are"} kept.` : ""}`}><Download size={12} /> {job.tax_year} only ({yearMatch})</button>}
          <button className="tbs-btn sm primary" onClick={onZip} disabled={actionBusy || ok === 0} title={offerYearZip ? "Every saved statement, whatever year is on it" : undefined}><Download size={12} /> Download ZIP{ok ? ` (${ok})` : ""}</button>
          {!running && <button className="tbs-btn sm danger" onClick={onDelete} disabled={actionBusy} title="Delete this run and its stored PDFs"><Trash2 size={12} /></button>}
        </div>
      </div>

      <div style={{ padding: "12px 16px", borderBottom: "1px solid #E8EAF1", display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="tbs-prog">
          <i className="ok" style={{ width: `${(ok / total) * 100}%` }} />
          <i className="err" style={{ width: `${(fail / total) * 100}%` }} />
          <i className="run" style={{ width: `${(inflight / total) * 100}%` }} />
        </div>
        <div className="tbs-stats">
          <div className="tbs-stat"><b>{job.done_count || 0}<span style={{ fontSize: 11, color: "#8E96AD", fontWeight: 600 }}> / {job.total}</span></b><span>done</span></div>
          <div className="tbs-stat ok"><b>{ok}</b><span>saved</span></div>
          <div className="tbs-stat err"><b>{fail}</b><span>failed</span></div>
          {yearWarn > 0 && <div className="tbs-stat warn"><b>{yearWarn}</b><span>other year</span></div>}
          {acctWarn > 0 && <div className="tbs-stat err"><b>{acctWarn}</b><span>account mismatch</span></div>}
          <span style={{ flex: 1 }} />
          {running && <span className="tbs-pill info"><Loader2 size={11} className="tbs-spin" /> running</span>}
          {job.status === "done" && <span className="tbs-pill ok">finished</span>}
          {job.status === "cancelled" && <span className="tbs-pill mute">stopped</span>}
          {job.status === "failed" && <span className="tbs-pill err">failed</span>}
        </div>
        {job.note && <div className="tbs-note"><Info size={13} /> {job.note}</div>}
        {yearWarn > 0 && (
          <div className="tbs-note"><AlertCircle size={13} />
            {yearWarn} statement{yearWarn === 1 ? "" : "s"} came back for a different tax year than {job.tax_year}. Those collectors have not posted {job.tax_year} bills yet. The file names carry the year that is actually on the statement.
          </div>
        )}
      </div>

      <div className="tbs-tbl-wrap">
        <table className="tbs-tbl">
          <thead>
            <tr><th>#</th><th>Collector</th><th>Account</th><th>Status</th><th>Year</th><th>Detail</th><th className="right">Time</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const c = collectorsByKey[r.collector];
              const isOpen = expanded[r.id];
              return (
                <React.Fragment key={r.id}>
                  <tr className={r.status === "running" ? "running" : ""}>
                    <td className="num">{i + 1}</td>
                    <td className="coll">{c ? c.name : r.collector}</td>
                    <td className="acct">{r.account}{r.year ? <small>year {r.year}</small> : null}</td>
                    <td><RowStatus r={r} taxYear={job.tax_year} /></td>
                    <td>{r.year_detected || (r.status === "saved" ? <span title="No text layer in the PDF, year could not be read" style={{ color: "#B8BFD0" }}>?</span> : "")}</td>
                    <td className={`msg ${r.status === "error" ? "bad" : ""}`}>
                      {r.status === "error" && (r.error_message || "")}
                      {r.status === "saved" && (r.warnings || []).filter((w) => !w.startsWith("statement is for tax year")).join("; ")}
                      {r.status === "saved" && r.scraped && (
                        <button className="tbs-link" style={{ marginLeft: 6 }} onClick={() => setExpanded((e) => ({ ...e, [r.id]: !isOpen }))}>{isOpen ? "hide data" : "bill data"}</button>
                      )}
                    </td>
                    <td className="right" style={{ color: "#8E96AD", fontSize: 11 }}>{fmtDuration(r.elapsed_ms)}</td>
                    <td className="right">
                      {r.status === "saved" && r.has_file && (
                        <>
                          <button className="tbs-icon-btn" title="Open statement" onClick={() => onOpenPdf(r)}><ExternalLink size={12} /></button>
                          <button className="tbs-icon-btn" title="Download statement" onClick={() => onDownloadPdf(r)}><FileDown size={12} /></button>
                        </>
                      )}
                      {r.source_url && r.status !== "saved" && (
                        <a className="tbs-icon-btn" href={r.source_url} target="_blank" rel="noreferrer" title="Open the collector page this came from"><ExternalLink size={12} /></a>
                      )}
                    </td>
                  </tr>
                  {isOpen && r.scraped && (
                    <tr><td /><td colSpan={7}><Scraped s={r.scraped} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
